import { useTranslation } from 'react-i18next';
import { Boxes, CalendarDays, Percent, TrendingUp, Wallet } from 'lucide-react';

import { KpiCard } from '@/components/shared/KpiCard';
import { useCurrency } from '@/hooks/useCurrency';
import type { SafetyStockSummaryRow } from '@/services/safetyStockService';
import {
  deltaVariant,
  formatDays,
  formatPercentFraction,
  formatSignedPercentFraction,
  formatUnits,
  toNumber,
} from '../utils/format';

interface Props {
  summary: SafetyStockSummaryRow | null;
  loading?: boolean;
  sourceHint?: string | null;
}

export function SafetyStockKpiCards({ summary, loading, sourceHint }: Props) {
  const { t, i18n } = useTranslation();
  const { formatRounded, symbol } = useCurrency();
  const locale = i18n.language || 'es';

  const value = summary?.safety_stock_value;
  const units = summary?.safety_stock_units;
  const pctProt = summary?.ss_pct_protection_value ?? summary?.ss_pct_protection_units;
  const days =
    summary?.coverage_days_median ??
    summary?.coverage_days_units ??
    summary?.coverage_days_value;
  const deltaValue = summary?.delta_value;
  const deltaPct = summary?.delta_value_pct;
  const variant = deltaVariant(deltaPct);

  const pctReason =
    summary?.no_data_reasons?.ss_pct_protection_value ||
    summary?.no_data_reasons?.ss_pct_protection_units;
  const daysReason =
    summary?.no_data_reasons?.coverage_days_units ||
    summary?.no_data_reasons?.coverage_days_value;

  const valuedNote =
    summary && summary.valued_items != null && summary.items != null
      ? t('safetyStock.summary.kpis.valuedNote', {
          valued: summary.valued_items,
          total: summary.items,
        })
      : undefined;

  return (
    <section className="space-y-2">
      {sourceHint ? <p className="text-xs text-gray-500">{sourceHint}</p> : null}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          title={t('safetyStock.summary.kpis.value', { currencySymbol: symbol })}
          value={toNumber(value) == null ? '—' : formatRounded(value)}
          isEuro={toNumber(value) != null}
          loading={loading}
          icon={<Wallet className="h-5 w-5" />}
          helpText={t('safetyStock.summary.kpis.valueHelp')}
          valueNote={valuedNote}
        />
        <KpiCard
          title={t('safetyStock.summary.kpis.units')}
          value={formatUnits(units, locale)}
          isCount
          loading={loading}
          icon={<Boxes className="h-5 w-5" />}
          helpText={t('safetyStock.summary.kpis.unitsHelp')}
        />
        <KpiCard
          title={t('safetyStock.summary.kpis.pctProtection')}
          value={pctReason ? '—' : formatPercentFraction(pctProt, locale)}
          loading={loading}
          icon={<Percent className="h-5 w-5" />}
          helpText={t('safetyStock.summary.kpis.pctProtectionHelp')}
          subtitle={
            pctReason
              ? t('safetyStock.summary.kpis.noDataProtection')
              : undefined
          }
        />
        <KpiCard
          title={t('safetyStock.summary.kpis.coverageDays')}
          value={daysReason && toNumber(days) == null ? '—' : formatDays(days, locale)}
          valueSuffix={
            toNumber(days) == null ? undefined : t('safetyStock.summary.kpis.daysUnit')
          }
          loading={loading}
          icon={<CalendarDays className="h-5 w-5" />}
          helpText={t('safetyStock.summary.kpis.coverageDaysHelp')}
          subtitle={
            daysReason && toNumber(days) == null
              ? t('safetyStock.summary.kpis.noDataDays')
              : undefined
          }
        />
        <KpiCard
          title={t('safetyStock.summary.kpis.delta')}
          value={toNumber(deltaValue) == null ? '—' : formatRounded(deltaValue)}
          isEuro={toNumber(deltaValue) != null}
          loading={loading}
          variant={variant}
          icon={<TrendingUp className="h-5 w-5" />}
          helpText={t('safetyStock.summary.kpis.deltaHelp', { currencySymbol: symbol })}
          subtitle={formatSignedPercentFraction(deltaPct, locale)}
        />
      </div>
    </section>
  );
}
